import {defineSlotRecipe} from '@chakra-ui/react3'
import {CONTENT_MOTION, inColumn, LEVEL_MOTION, RESOLVED_ITEM_INDEX} from './flyout-stagger'

/**
 * Slot recipe for the level-2 pane of the flyout navigation.
 *
 * The pane holds three columns — the main teaser, the navigation-item list, and
 * the optional teaser column. Mobile stacks them under the drill-out
 * breadcrumb; desktop sits them side by side to the right of the level-1 list,
 * which stays visible (see `flyout-navigation.recipe.js` for how the two panes
 * are placed relative to each other).
 *
 * Per STOK-17026 the whole pane scrolls as one page, with the teaser columns
 * pinned to the top while the item list scrolls past them. The flyout `body`
 * is the scroll container the stickiness resolves against.
 *
 * All three columns are sticky, `items` included (STOK-17341). That does not
 * contradict §4.2.1's "4.2.2 scrolls as a list": the tallest column is the one
 * that sets the row's height, so it has no travel and scrolls as a list whether
 * or not it is sticky — and the list is normally the tallest. Sticky only
 * changes the case where it is not.
 *
 * That covers the scroll this pane's own height causes. The scroll the taller
 * level-1 list causes is answered a level up, by the sticky on
 * `level2Container` in `flyout-navigation.recipe.js` — the columns here cannot,
 * because their containing block is this pane's own row.
 */
export const flyoutNavigationLevel2Recipe = defineSlotRecipe({
    className: 'flyout-navigation-level2',
    slots: [
        'root',
        'breadcrumb',
        'breadcrumbIcon',
        'columns',
        'mainTeaser',
        'items',
        'item',
        'teasers'
    ],
    base: {
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-24'
        },
        breadcrumb: {
            // Mobile-only, and the first thing in the pane, so it leads its
            // cascade in the same slot the level-1 list occupies on desktop.
            ...inColumn(0),
            ...CONTENT_MOTION,
            ...LEVEL_MOTION,
            // Desktop keeps the level-1 list on screen, so there is nothing to
            // drill back out to — the control only exists in the mobile pane model.
            display: {base: 'flex', s: 'none'},
            alignItems: 'center',
            gap: 'spacing-8',
            width: 'full',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            textAlign: 'start',
            padding: 0,
            color: 'text.primary',
            textStyle: 'heading.largeItalic'
        },
        breadcrumbIcon: {
            flexShrink: 0,
            _icon: {
                // Without an explicit size the chakra('svg') wrapper emits no
                // class and the reset collapses the SVG to zero dimensions.
                width: '1.5rem',
                height: '1.5rem'
            }
        },
        columns: {
            display: 'flex',
            flexDirection: {base: 'column', s: 'row'},
            alignItems: {s: 'flex-start'},
            gap: {base: 'spacing-24', s: 'var(--flyout-column-gap)'},
            // STOK-17026 sizes every column as a share of the content
            // container, but this row is not that container: `panes` gives 15%
            // to the level-1 list and the rest, less one gap, to the pane this
            // row fills. This recovers the container's own width, so each
            // column below states the share the spec gives it directly. The
            // percentage resolves against this row at the point each column
            // uses it, which is exactly the width the reconstruction assumes.
            // `--flyout-column-gap` is the same seam `panes` puts between the
            // level-1 list and this pane, inherited from `container`.
            '--flyout-content-width': 'calc((100% + var(--flyout-column-gap)) / 0.85)'
        },
        mainTeaser: {
            // First of the pane's three columns in the cascade. A single teaser,
            // so it animates as one unit rather than per row.
            ...inColumn(1),
            ...CONTENT_MOTION,
            ...LEVEL_MOTION,
            // Mobile shows the landscape crop full width; desktop gives the
            // portrait crop its own column beside the list — column 4.2.1, 25%.
            flex: {s: '0 0 calc(var(--flyout-content-width) * 0.25)'},
            width: 'full',
            height: 'auto',
            borderRadius: 's',
            overflow: 'hidden',
            position: {s: 'sticky'},
            // Pins at the flyout's own top inset, not at the scrollport edge,
            // so the column keeps the gap to the bar it has before it pins.
            top: {s: 'var(--flyout-scroll-inset, 0px)'},
            // Desktop holds the column's width with nothing in it, so the item
            // list starts at the same place whether or not the module carries a
            // main teaser. Mobile stacks the columns, where an empty one would
            // only contribute a gap, so there it collapses.
            _empty: {
                display: {base: 'none', s: 'block'}
            }
        },
        items: {
            // The column itself does not animate — its rows do, each on its own
            // delay. Custom properties inherit, so declaring the column index
            // here is what places every row beneath it in the cascade.
            ...inColumn(2),
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-6',
            listStyleType: 'none',
            margin: 0,
            padding: 0,
            // Column 4.2.2, 35%. It may shrink below that share on a narrow
            // desktop, where the fixed gaps eat into it, but never grows past
            // it.
            flex: {s: '0 1 calc(var(--flyout-content-width) * 0.35)'},
            // Above ~1200px of content the share overshoots what the design
            // draws: every frame plateaus the column at 420px (419 at 1440,
            // 420 at 1920, where the container is ~280px wider). Past that the
            // surplus belongs in the run-up to the teaser column, which
            // `teasers` holds at the inline end, not in the cards. The cap sits
            // on the column because the cards fill whatever it gives them.
            maxWidth: {s: 'navigation-card-column-max-width'},
            // keeps a long title from stretching the column past its share
            minWidth: 0,
            // Sticky like its neighbours, so whichever column is shorter than
            // the row pins while the tallest one scrolls the panel past it
            // (STOK-17341). When this is the tallest — the usual case, and the
            // one §4.2.1 describes as "4.2.2 scrolls as a list" — it is what
            // sets the row's height, so there is no travel here and it scrolls
            // as a list either way.
            position: {s: 'sticky'},
            top: {s: 'var(--flyout-scroll-inset, 0px)'},
            _empty: {
                display: 'none'
            }
        },
        item: {
            // A box for the motion to apply to, asked of `NavigationHide` by the
            // property it reads — see the level-1 `item` slot for why it is not
            // simply `display`.
            '--navigation-hide-display': 'block',
            minWidth: 0,
            // Column index inherits from `items`; the row index is inline.
            ...RESOLVED_ITEM_INDEX,
            ...CONTENT_MOTION,
            ...LEVEL_MOTION
        },
        teasers: {
            // Last of the pane's columns in the cascade.
            ...inColumn(3),
            ...CONTENT_MOTION,
            ...LEVEL_MOTION,
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-12',
            // Column 4.2.3, 15%.
            flex: {s: '0 0 calc(var(--flyout-content-width) * 0.15)'},
            // The column sits flush to the inline end at every breakpoint. It
            // only needs saying once `items` can stop short of it: without this
            // the two would pack together and leave the trailing space — and the
            // country selector that paints into it — past this column.
            marginInlineStart: {s: 'auto'},
            // The teaser inside fills this column rather than naming a width,
            // so without this the column's automatic minimum comes from the
            // image's intrinsic contribution and can floor it above its share.
            minWidth: 0,
            position: {s: 'sticky'},
            top: {s: 'var(--flyout-scroll-inset, 0px)'},
            // The desktop tool-nav row's country selector sits at the inline
            // end of the panel, over this column (STOK-17026 §5, "space
            // reserved in column 4.2.3"), so hold that band clear. Unlike the
            // level-1 reserve this column does not stretch — `columns` aligns
            // to `flex-start` — so the padding only lands in the row's band
            // when this column happens to be the tallest, which is also the
            // only case where the two would collide.
            // TODO(STOK-17301): derive this from the row's measured height.
            paddingBottom: {s: 'spacing-60'},
            // Desktop holds the column's width with nothing in it, so the row
            // below never paints over the item list — `items` already stops in
            // the same place in every module, from its own capped share.
            // Mobile stacks the columns, where an empty one would only
            // contribute a gap, so there it collapses.
            _empty: {
                display: {base: 'none', s: 'flex'}
            }
        }
    }
})
